import { CodeBlock, CopyBlock, monokai } from "react-code-blocks";
import { Link, useOutletContext } from "react-router-dom";
import {
    closeStream,
    closeStreamResponse,
    exampleQuery,
    exampleQueryResponse,
    startStreamMessage,
    streamingURL,
    updateMessages
} from "../Assets/apiDefinitionExamples";
import { LATEST_V2_MINOR_VERSION, PageNames, TabNames } from "../Assets/variables";
import { useEffect } from "react";

const APIDefinition = () => {
    const {setActiveTabItem} = useOutletContext();

    useEffect(() => {
        setActiveTabItem(TabNames.APIDefinition);
    }, []);

    return (
        <div id="tab-definition" class="api-panel">
            <div className="api-definition-container">
                <p>Streaming Market Data API will provide streaming access to latest prices through a web socket connection.</p>
                <p>You need a client to receive the streaming data.</p>
                <p>Details on getting started with a streaming client can be found in our <Link to={`/${PageNames.Documentation}#web-socket-client-example`}>documentation</Link>.</p>

                <span className="heading-2">Streaming URL</span>
                <p>Below is the web socket URL for the streaming market data API</p>

                <div className="code-example">
                    <CopyBlock
                        text={streamingURL}
                        language={"text"}
                        showLineNumbers={false}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <span className="heading-2">Start Stream</span>
                <p>You can start the stream by sending a <strong>startStream</strong> message.</p>
                <p>Below is an example of a <strong>startStream</strong> message that requests high and low prices for ABB</p>

                <div className="code-example">
                    <CopyBlock
                        text={startStreamMessage}
                        language={"text"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <p>The responses will have a START message followed by UPDATE messages.</p>

                <div className="code-example">
                    <CodeBlock
                        text={updateMessages}
                        language={"text"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <span className="heading-3">Start Stream Parameters</span>

                <table>
                    <thead>
                        <tr>
                            <th>Parameter</th>
                            <th>Type</th>
                            <th>Available Values</th>
                            <th>Example</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>streamId</td>
                            <td>string</td>
                            <td></td>
                            <td>stream1, NASDAQ_Equities</td>
                            <td>The Stream ID can be provided as one of the input parameters to identify the stream. The Stream ID can be used to close a specific stream.</td>
                        </tr>
                        <tr>
                            <td>scheme*</td>
                            <td>enum string</td>
                            <td>ISIN_BC, VALOR_BC, SEDOL_BC, CUSIP_BC, TICKER_BC</td>
                            <td>TICKER_BC</td>
                            <td>Scheme of the Listing Identifier. It is a combination of the instrument scheme and the market scheme separated by underscore.</td>
                        </tr>
                        <tr>
                            <td>ids*</td>
                            <td>array[string]</td>
                            <td></td>
                            <td>ABBN_4, 12222171_4</td>
                            <td>Listing Identifier to be provided by client as input parameter. Several identifiers can be requested comma-separated.</td>
                        </tr>
                        <tr>
                            <td>conflationType</td>
                            <td>enum string</td>
                            <td>NO_CONFLATION, INTERVAL</td>
                            <td>INTERVAL</td>
                            <td>Conflation Type is an optional parameter. By default all messages are sent without any conflation. For interval conflation, you have to provide the conflation period as a string in ISO 8601 duration format in the input parameter Conflation Period.</td>
                        </tr>
                        <tr>
                            <td>conflationPeriod</td>
                            <td>string</td>
                            <td></td>
                            <td>PT1S</td>
                            <td>
                                <p>Provided as string in ISO 8601 duration format. PT<i>n</i>M<i>n</i>S</p>
                                <p>The minimum conflation period possible is 100 milliseconds and the maximum being 1 minute. Any period shorter than 1 minute ("PT1M") has to be requested in seconds (e.g. "PT30S"). Examples are:</p>
                                <ul>
                                    <li>PT1M = 1 minute</li>
                                    <li>PT1S = 1 second</li>
                                    <li>PT0.5S = 500 milliseconds</li>
                                    <li>PT0.1S = 100 milliseconds</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>api-version</td>
                            <td>string</td>
                            <td></td>
                            <td>{LATEST_V2_MINOR_VERSION}</td>
                            <td>Version of the API as date in ISO 8601 format.</td>
                        </tr>
                    </tbody>
                </table>

                <span className="heading-3">Response Fields</span>
                <p>While creating the query, you can select the fields you would like to see in the response.</p>
                <p>The available price fields are described in the 'Content' tab</p>
                <p>Apart from the price fields the below additional fields can be requested as part of the query.</p>

                <table>
                    <thead>
                        <tr>
                            <th>Field Name</th>
                            <th>Sub Fields</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>streamId</td>
                            <td>string</td>
                            <td>
                                <ul>
                                    <li>In order to stream different fields for different listings, it is possible to "tag" every message with a <strong>streamId</strong>. This is delivered as part of every message.</li>
                                    <li>This enables advanced workflow, like the possibility to start / stop multiple stream within the same WebSocket connection identified by a unique <strong>streamId</strong>.</li>
                                    <li>If the streamId is not unique within the current WebSocket connection, an error is raised.</li>
                                    <li>The stream ID can be defined as an input parameter while composing the query.</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>type</td>
                            <td></td>
                            <td>
                                Type of the message:
                                <ul>
                                    <li><strong>START</strong>: this is delivered only by <strong>startStream</strong> subscription and it represents the "initial image" of the market stream. All data, including reference data, is delivered in this message </li>
                                    <li><strong>UPDATE</strong>: this is delivered only by <strong>startStream</strong> subscription and it represent an update. Reference data is not delivered in this message.</li>
                                    <li><strong>STOP</strong>: this is delivered only by <strong>closeStream</strong> mutation and represents the end message of the stream.</li>
                                    <li><strong>ERROR</strong>: delivered in case it is not possible to resolve the requested listing (e.g. wrong isin)</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>requestedId</td>
                            <td></td>
                            <td>Listing Identification provided as input parameter.</td>
                        </tr>
                        <tr>
                            <td>requestedScheme</td>
                            <td></td>
                            <td>Listing Scheme provided as input parameter.</td>
                        </tr>
                        <tr>
                            <td rowSpan={5}>lookup</td>
                            <td></td>
                            <td>lookup provides key reference data fields about the listing. The below are the fields that are available in lookup</td>
                        </tr>
                        <tr>
                            <td>listingName</td>
                            <td>Listing short name with up to 19 characters.</td>
                        </tr>
                        <tr>
                            <td>marketName</td>
                            <td>Market short name with up to 19 characters.</td>
                        </tr>
                        <tr>
                            <td>listingCurrency</td>
                            <td>The trading currency according to ISO4217 alpha codes.</td>
                        </tr>
                        <tr>
                            <td>listingStatus</td>
                            <td>Indicating the current state of the listing, e.g. listing applied for, listing effective, delisted, etc.</td>
                        </tr>
                        <tr>
                            <td>timestamp</td>
                            <td></td>
                            <td>
                                <p>Generally, in real-time feeds, the providers disseminate various timestamps that are associated with different price types or events. There could be multiple timestamps associated with a given price.</p>
                                <p>Different providers may have different names for these timestamps and will not necessarily provide all of them in their feeds. In some cases, if the timestamp is not provided by the exchange, SIX internal timestamp may be used.</p>
                                <p>In SIX products only one <strong>timestamp</strong> can be displayed with a given price or event. If the exchange provides multiple timestamps within the feed, use the timestamps in the following priority:</p>
                                <ul>
                                    <li>Market data entry time - the time when the price was entered on a trader's side.</li>
                                    <li>Transaction time - the time when the price was recognized by the exchange.</li>
                                    <li>Exchange processing time - the time when the price was processed for updates, calculations at the exchange.</li>
                                    <li>Exchange's sending time - the time that the exchange disseminate the price via the feed.</li>
                                </ul>
                            </td>
                        </tr>
                        <tr>
                            <td>unixTimestamp</td>
                            <td></td>
                            <td>The <strong>unixTimestamp</strong> is the timestamp representation in Unix time. Both timestamp and unixTimestamp can be requested for all prices</td>
                        </tr>
                    </tbody>
                </table>

                <span className="heading-3">Example Query</span>
                <p>Below is an example query with all the fields that are available:</p>

                <div className="code-example">
                    <CopyBlock
                        text={exampleQuery}
                        language={"text"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <p>Below is the sample response for the above query:</p>

                <div className="code-example">
                    <CodeBlock
                        text={exampleQueryResponse}
                        language={"text"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <span className="heading-2">Close Stream</span>
                <p>In order to close a stream, send the below <strong>closeStream</strong> query as a mutation along with the streamId.</p>

                <div className="code-example">
                    <CopyBlock
                        text={closeStream}
                        language={"text"}
                        showLineNumbers={true}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>

                <p>You will receive a STOP message upon closing the stream</p>

                <div className="code-example">
                    <CodeBlock
                        text={closeStreamResponse}
                        language={"text"}
                        showLineNumbers={false}
                        startingLineNumber={1}
                        theme={monokai}
                        codeBlock={true}
                    />
                </div>
            </div>
        </div>
    );
}

export default APIDefinition;
